import { type Ref, nextTick, onBeforeUnmount } from "vue";

export const useHeaderVisibilityToggle = (
  scrollRoot: Ref<Element | undefined>,
  bigHeader: Ref<Element | undefined>,
  simpleHeader: Ref<Element | undefined>,
  threshold: number = 0.6,
) => {
  let observer: IntersectionObserver | undefined;

  const updateVisibility = () => {
    if (!simpleHeader.value || !bigHeader.value) return;
    const rootEl: Element | null = scrollRoot.value ?? document.documentElement;
    if (!rootEl) return;
    const rootRect = rootEl.getBoundingClientRect();
    const elRect = bigHeader.value.getBoundingClientRect();
    const visibleHeight = Math.min(elRect.bottom, rootRect.bottom) - Math.max(elRect.top, rootRect.top);
    const ratio = elRect.height > 0 ? Math.max(0, Math.min(1, visibleHeight / elRect.height)) : 0;
    const shouldHideSimple = ratio > threshold;
    simpleHeader.value.classList.toggle("hide", shouldHideSimple);
  };

  const init = async () => {
    await nextTick();
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const shouldHideSimple = entry.intersectionRatio > threshold;
          if (simpleHeader.value) {
            simpleHeader.value.classList.toggle("hide", shouldHideSimple);
          }
        });
      },
      {
        root: scrollRoot.value ?? undefined,
        threshold: [0, 0.25, 0.5, threshold, 0.75, 1],
      },
    );

    if (bigHeader.value) {
      observer.observe(bigHeader.value);
    }

    updateVisibility();

    if (scrollRoot.value) {
      scrollRoot.value.addEventListener("scroll", updateVisibility, { passive: true });
    }
    window.addEventListener("resize", updateVisibility, { passive: true });
  };

  const observe = () => {
    if (observer && bigHeader.value) {
      observer.observe(bigHeader.value);
    }
  };

  const cleanup = () => {
    if (observer) {
      observer.disconnect();
      observer = undefined;
    }
    if (scrollRoot.value) {
      scrollRoot.value.removeEventListener("scroll", updateVisibility);
    }
    window.removeEventListener("resize", updateVisibility);
  };

  onBeforeUnmount(() => cleanup());

  return { init, observe, updateVisibility, cleanup };
};
